import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import { ConversationCreateForm } from '../components/ConversationCreateForm';
import { ConversationTable } from '../components/ConversationTable';
import type { AppAccessSummary, AppItem, Conversation, ConversationAccessSummary } from '../types';
import { conversationControlAccess } from '../utils/conversationAccess';
import { getConversationListResource } from '../utils/conversationListResource';
import { reportErrorLocal } from '../utils/errors';
import { useLiveResource } from '../utils/liveResource';
import { canCreateConversation } from '../utils/projectAccess';
import './ConversationsHome.lane34.css';

type NotificationState = {
  notification?: {
    type: 'success' | 'info' | 'warning';
    message: string;
    link?: {
      to: string;
      label: string;
    };
  };
};

export default function ConversationsHome({
  apiClient,
  setError
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [apps, setApps] = useState<AppItem[]>([]);
  const [conversationCreationApps, setConversationCreationApps] = useState<AppItem[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [conversationAccessById, setConversationAccessById] = useState<
    Record<string, ConversationAccessSummary>
  >({});
  const [pageError, setPageError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [gitlabRefreshBusy, setGitlabRefreshBusy] = useState(false);
  const [notification, setNotification] = useState<NotificationState['notification'] | null>(
    (location.state as NotificationState)?.notification ?? null
  );
  const staleConversationsByKeyRef = useRef<Map<string, Conversation[]>>(new Map());
  const lastVisibleConversationsRef = useRef<Conversation[]>([]);
  const appsLoadGenerationRef = useRef(0);

  // Clear notification from location state after reading it
  useEffect(() => {
    if ((location.state as NotificationState)?.notification) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const conversationResource = useMemo(
    () =>
      getConversationListResource(apiClient, {
        includeArchived,
        searchQuery
      }),
    [apiClient, includeArchived, searchQuery]
  );
  const conversationSnapshot = useLiveResource(conversationResource.live);
  const conversations =
    conversationSnapshot.data?.conversations ??
    staleConversationsByKeyRef.current.get(conversationResource.key) ??
    lastVisibleConversationsRef.current;

  useEffect(() => {
    if (!conversationSnapshot.data) {
      return;
    }
    const next = conversationSnapshot.data.conversations;
    staleConversationsByKeyRef.current.set(conversationResource.key, next);
    lastVisibleConversationsRef.current = next;
  }, [conversationResource.key, conversationSnapshot.data]);

  const loadApps = useCallback(async () => {
    const generation = appsLoadGenerationRef.current + 1;
    appsLoadGenerationRef.current = generation;
    setAppsLoading(true);
    try {
      const data = await apiClient.get<AppItem[]>('/api/apps');
      if (appsLoadGenerationRef.current !== generation) {
        return;
      }
      setApps(data);
      const allowedApps = await Promise.all(
        data.map(async (app) => {
          try {
            const access = await apiClient.get<AppAccessSummary>(`/api/apps/${app.id}/access`);
            return canCreateConversation(access) ? app : null;
          } catch {
            // A revoked membership or failed access lookup must not leave a
            // project selectable for conversation creation.
            return null;
          }
        })
      );
      if (appsLoadGenerationRef.current !== generation) {
        return;
      }
      setConversationCreationApps(allowedApps.filter((app): app is AppItem => app !== null));
    } catch (err) {
      if (appsLoadGenerationRef.current !== generation) {
        return;
      }
      setApps([]);
      setConversationCreationApps([]);
      reportErrorLocal('Failed to load apps', err, setError, setPageError);
    } finally {
      if (appsLoadGenerationRef.current === generation) {
        setAppsLoading(false);
      }
    }
  }, [apiClient, setError]);

  async function refreshConversations() {
    const next = await conversationResource.live.refresh('manual');
    if (next.error) {
      throw new Error(next.error);
    }
  }

  useEffect(() => {
    let canceled = false;
    (async () => {
      await loadApps();
      if (canceled) {
        return;
      }
    })();
    return () => {
      canceled = true;
    };
  }, [loadApps]);

  useEffect(() => {
    if (!conversationSnapshot.error) {
      return;
    }
    // A list error must not preserve project titles/status/GitLab data from a
    // membership state that the server can no longer confirm.
    staleConversationsByKeyRef.current.clear();
    lastVisibleConversationsRef.current = [];
    setConversationAccessById({});
    conversationResource.live.invalidate('conversation-list-error-fail-closed');
    setPageError(conversationSnapshot.error);
  }, [conversationResource, conversationSnapshot.error]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      // Polling deliberately pauses in the background. Treat foregrounding as
      // a fresh authorization boundary and hide cached project metadata before
      // either membership-filtered request completes.
      staleConversationsByKeyRef.current.clear();
      lastVisibleConversationsRef.current = [];
      setConversationAccessById({});
      setApps([]);
      setConversationCreationApps([]);
      void Promise.all([conversationResource.reauthorize(), loadApps()]).catch((err) => {
        reportErrorLocal(
          'Failed to refresh project authorization',
          err,
          setError,
          setPageError
        );
      });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [conversationResource, loadApps, setError]);

  useEffect(() => {
    const ids = Array.from(new Set(conversations.map((conversation) => conversation.id)));
    setConversationAccessById({});
    if (ids.length === 0) {
      return;
    }

    let cancelled = false;
    void Promise.all(
      ids.map(async (conversationId) => {
        try {
          const access = await apiClient.get<ConversationAccessSummary>(
            `/api/conversations/${conversationId}/access`
          );
          return [conversationId, access] as const;
        } catch {
          // Lifecycle controls remain hidden when access is revoked or the
          // resource lookup cannot establish its current permissions.
          return null;
        }
      })
    ).then((entries) => {
      if (cancelled) {
        return;
      }
      setConversationAccessById(
        Object.fromEntries(
          entries.filter(
            (entry): entry is readonly [string, ConversationAccessSummary] => entry !== null
          )
        )
      );
    });
    return () => {
      cancelled = true;
    };
  }, [apiClient, conversations]);

  async function deleteConversation(conversationId: string) {
    if (
      !confirm(
        'Are you sure you want to delete this conversation? This will also delete all messages, runs, and associated data.'
      )
    ) {
      return;
    }
    try {
      await apiClient.del(`/api/conversations/${conversationId}`);
      conversationResource.live.updateLocal(
        (prev) => ({
          conversations: (prev?.conversations ?? []).filter(
            (conversation) => conversation.id !== conversationId
          )
        }),
        'delete-conversation'
      );
    } catch (err) {
      reportErrorLocal('Failed to delete conversation', err, setError, setPageError);
    }
  }

  async function archiveConversation(conversationId: string) {
    try {
      const updated = await apiClient.post<Conversation>(`/api/conversations/${conversationId}/archive`, {});
      conversationResource.live.updateLocal(
        (prev) => ({
          conversations: (prev?.conversations ?? []).map((conversation) =>
            conversation.id === conversationId ? updated : conversation
          )
        }),
        'archive-conversation'
      );
    } catch (err) {
      reportErrorLocal('Failed to archive conversation', err, setError, setPageError);
    }
  }

  async function unarchiveConversation(conversationId: string) {
    try {
      const updated = await apiClient.post<Conversation>(`/api/conversations/${conversationId}/unarchive`, {});
      conversationResource.live.updateLocal(
        (prev) => ({
          conversations: (prev?.conversations ?? []).map((conversation) =>
            conversation.id === conversationId ? updated : conversation
          )
        }),
        'unarchive-conversation'
      );
    } catch (err) {
      reportErrorLocal('Failed to unarchive conversation', err, setError, setPageError);
    }
  }

  const appNameFor = (appId: string) => {
    const app = apps.find((item) => item.id === appId);
    return app ? app.name || app.slug : appId;
  };

  async function recalculateGitlabStatus() {
    setGitlabRefreshBusy(true);
    try {
      await conversationResource.forceRefreshGitlab();
    } catch (err) {
      reportErrorLocal('Failed to load GitLab status', err, setError, setPageError);
    } finally {
      setGitlabRefreshBusy(false);
    }
  }

  return (
    <section className="grid conversations-home-34" aria-labelledby="conversations-home-34-title">
      <div className="card full conversation-list-card">
        <div className="card-header conversations-home-34__header">
          <div>
            <span className="conversations-home-34__eyebrow">Conversation workspace</span>
            <h2 id="conversations-home-34-title">Conversations</h2>
            <p>Start a conversation or open an existing one to continue.</p>
          </div>
          <div className="row">
            {apiClient.mode !== 'tool-browser' && <button className="ghost" onClick={recalculateGitlabStatus} disabled={gitlabRefreshBusy}>
              {gitlabRefreshBusy ? 'Recalculating…' : 'Recalculate GitLab'}
            </button>}
            <button
              className="ghost"
              onClick={() => {
                refreshConversations().catch((err) =>
                  reportErrorLocal('Failed to load conversations', err, setError, setPageError)
                );
              }}
            >
              Refresh
            </button>
          </div>
        </div>
        {notification && (
          <div className={`notification notification--${notification.type}`}>
            <span>{notification.message}</span>
            {notification.link && (
              <Link to={notification.link.to}>{notification.link.label}</Link>
            )}
            <button
              type="button"
              className="ghost compact"
              onClick={() => setNotification(null)}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        )}
        {pageError && <ErrorBanner message={pageError} onDismiss={() => setPageError(null)} />}
        {!appsLoading && conversationCreationApps.length > 0 && (
          <section className="conversations-home-34__create" aria-labelledby="conversations-home-34-create-title">
            <h3 id="conversations-home-34-create-title">Start a conversation</h3>
            <p className="conversations-home-34__help">Give it a title, then choose the experience, visibility and project scope.</p>
            <ConversationCreateForm
              apiClient={apiClient}
              apps={conversationCreationApps}
              allowNoApp={conversationCreationApps.some((app) => app.is_self === true)}
              onCreated={(created) => navigate(`/conversations/${created.id}`)}
              setError={setError}
            />
          </section>
        )}
        {!appsLoading && conversationCreationApps.length === 0 && (
          <div className="hint">
            You do not currently have permission to create conversations in an available project.
          </div>
        )}
        <section className="conversations-home-34__list" aria-labelledby="conversations-home-34-list-title">
          <div className="conversations-home-34__list-heading">
            <h3 id="conversations-home-34-list-title">Browse conversations</h3>
            <p className="conversations-home-34__help">Open to continue · MR and Build show GitLab status</p>
          </div>
          <div className="filters conversations-home-34__filters">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
              Show archived
            </label>
            <label className="conversations-home-34__search">
              <span>Search conversations</span>
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </label>
          </div>
          <ConversationTable
            conversations={conversations}
            showAppColumn
            appLabelFor={appNameFor}
            onDelete={deleteConversation}
            onArchive={archiveConversation}
            onUnarchive={unarchiveConversation}
            canManageConversation={(conversation) =>
              conversationControlAccess(conversationAccessById[conversation.id]).canManageConversation
            }
            canArchiveConversation={apiClient.mode === 'tool-browser' ? (conversation) =>
              conversationAccessById[conversation.id]?.browser_operations?.archive === true
              : undefined}
          />
        </section>
      </div>
    </section>
  );
}
